import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Home, Package, FileText, Box, ShoppingCart, ShoppingBag, Activity, BarChart3, Users, LogOut, ClipboardList, Factory, FileCheck, Shield, Menu, X, Contact, MapPin, ChevronLeft, ChevronRight, User as UserIcon, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { motion, AnimatePresence } from 'framer-motion';
import GlobalSearch from './GlobalSearch';
import Breadcrumbs from './ui/Breadcrumbs';

const Layout = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const hasViewPermission = (module) => {
    if (user?.role === 'admin') return true;
    const perms = user?.permissions || {};
    if (typeof perms !== 'object' || Array.isArray(perms)) return false;
    return (perms[module] || []).includes('view');
  };

  const allNavItems = [
    { path: '/', label: 'Dashboard', icon: Home, module: 'dashboard' },
    { path: '/raw-materials', label: 'Raw Materials', icon: Package, module: 'raw_materials' },
    { path: '/raw-material-inventory', label: 'Material Inventory', icon: Activity, module: 'raw_material_inventory' },
    { path: '/bom', label: 'Bill of Materials', icon: FileText, module: 'bom' },
    { path: '/finished-goods', label: 'Finished Goods', icon: Box, module: 'finished_goods' },
    { path: '/purchase-requisitions', label: 'Purchase Requisitions', icon: FileCheck, module: 'purchase_orders' },
    { path: '/purchase-orders', label: 'Purchase Orders', icon: ShoppingCart, module: 'purchase_orders' },
    { path: '/customers', label: 'Customers', icon: Contact, module: 'customers' },
    { path: '/warehouse-locations', label: 'Warehouse Locations', icon: MapPin, module: 'warehouse_locations' },
    { path: '/sales-orders', label: 'Sales Orders', icon: ShoppingBag, module: 'sales_orders' },
    { path: '/production-orders', label: 'Production Orders', icon: ClipboardList, module: 'production' },
    { path: '/production', label: 'Production Tracking', icon: Factory, module: 'production' },
    { path: '/completed-production', label: 'Completed Production', icon: BarChart3, module: 'production' },
    { path: '/catalog-info', label: 'Catalog Info', icon: FileText, module: 'catalog' },
    { path: '/brochure-maker', label: 'Brochure Maker', icon: FileText, module: 'catalog' },
    { path: '/reports', label: 'Reports', icon: BarChart3, module: 'reports' },
  ];

  if (user?.role === 'admin') {
    allNavItems.push({ path: '/users', label: 'Users', icon: Users, module: 'users' });
    allNavItems.push({ path: '/roles', label: 'Roles & Permissions', icon: Shield, module: 'users' });
  }

  const navItems = allNavItems.filter(item => hasViewPermission(item.module));

  return (
    <div className="flex h-screen bg-[hsl(40,20%,94%)]">
      {/* Mobile top bar */}
      <div className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3 bg-[hsl(40,20%,97%)] border-b border-stone-200 lg:hidden">
        <h1 className="text-lg font-semibold text-[hsl(145,60%,20%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Stock Manager</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSearchOpen(true)}
            className="p-2 rounded-md text-[hsl(145,20%,40%)] hover:bg-stone-200/50 transition-colors"
          >
            <Search className="lucide lucide-search" size={20} />
          </button>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md text-[hsl(145,20%,40%)] hover:bg-stone-200/50 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Overlay backdrop for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full bg-[hsl(40,20%,97%)] border-r border-stone-200 flex flex-col
          transform transition-all duration-300 ease-in-out
          lg:translate-x-0 lg:static lg:z-auto
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-64'}
        `}
        data-testid="sidebar"
      >
        <div className={`p-6 border-b border-stone-200 flex flex-col gap-4 ${isCollapsed ? 'items-center px-4' : ''}`}>
          <div className="flex items-center w-full justify-between">
            {!isCollapsed && <h1 className="text-2xl font-semibold text-[hsl(145,60%,20%)] tracking-tight whitespace-nowrap" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="app-title">Stock Manager</h1>}
            {isCollapsed && <h1 className="text-2xl font-bold text-[hsl(145,60%,20%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>SM</h1>}
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-md text-[hsl(145,20%,40%)] hover:bg-stone-200/50 lg:hidden ml-auto"
            >
              <X size={20} />
            </button>
          </div>

          {!isCollapsed ? (
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden lg:flex items-center gap-2 w-full px-3 py-2 text-sm text-stone-400 bg-white border border-stone-200 rounded-md shadow-sm hover:bg-stone-50 hover:border-stone-300 transition-colors"
            >
              <Search className="lucide lucide-search shrink-0" size={16} />
              <span className="flex-1 text-left">Search...</span>
              <kbd className="hidden lg:inline-flex h-5 items-center gap-1 rounded border bg-stone-100 px-1.5 font-mono text-[10px] font-medium text-stone-500 opacity-100">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden lg:flex p-2 rounded-md text-stone-400 bg-white border border-stone-200 hover:bg-stone-50 hover:text-[hsl(145,60%,20%)] transition-colors justify-center"
              title="Search (Cmd+K)"
            >
              <Search className="lucide lucide-search shrink-0" size={18} />
            </button>
          )}
        </div>

        <TooltipProvider delayDuration={0}>
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto overflow-x-hidden scrollbar-hide" data-testid="navigation">
            {navItems.map((item) => {
              const Icon = item.icon;
              const link = (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-sm text-sm font-medium transition-colors duration-200 ${isCollapsed ? 'justify-center w-10 h-10 mx-auto p-0 mb-2' : 'px-4 py-2.5'} ${isActive
                      ? 'bg-[hsl(145,60%,20%)] text-white'
                      : 'text-[hsl(145,20%,40%)] hover:bg-stone-200/50 hover:text-stone-900'
                    }`
                  }
                  title={isCollapsed ? "" : undefined}
                >
                  <Icon size={18} className="shrink-0" />
                  {!isCollapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              );

              if (isCollapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right" className="ml-2 bg-[hsl(145,60%,15%)] text-white border-none">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return link;
            })}
          </nav>
        </TooltipProvider>

        <div className="p-4 border-t border-stone-200 flex flex-col gap-2">
          {/* Collapse Toggle (Desktop only) */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`hidden lg:flex items-center justify-center p-2 rounded-md text-stone-400 hover:bg-stone-200/50 hover:text-stone-700 transition-colors w-full mb-2 ${isCollapsed ? '' : 'self-end w-auto mx-2'}`}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>

          {/* User Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-3 p-2 rounded-md hover:bg-stone-200/50 transition-colors w-full ${isCollapsed ? 'justify-center px-0' : 'text-left'}`}>
                <div className="w-8 h-8 rounded-full bg-[hsl(145,60%,20%)] text-white flex items-center justify-center shrink-0 font-medium">
                  {user?.name?.charAt(0).toUpperCase() || <UserIcon size={16} />}
                </div>
                {!isCollapsed && (
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-900 truncate">{user?.name}</div>
                    <div className="text-xs text-stone-500 capitalize">{user?.role}</div>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="right" sideOffset={10} className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user?.name}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user?.mobile}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer focus:bg-red-50 focus:text-red-700">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
      {/* Main content with Framer Motion Page Transitions */}
      <main className="flex-1 overflow-auto pt-14 lg:pt-0 bg-[#fbfbfb] relative">
        <div className="px-8 pt-6 pb-2">
          <Breadcrumbs />
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="min-h-full"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Global Search Dialog */}
      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} hasViewPermission={hasViewPermission} />
    </div>
  );
};

export default Layout;